/**
 * /broadcasts — list with status + counters. Cycle 12.
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";
import { useAuth } from "../../../lib/auth-context.js";

interface BroadcastRow {
  id: string;
  name: string;
  status: string;
  igAccountId: string;
  scheduledAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  eligibleCount: number | null;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  failureReason: string | null;
  createdAt: string;
}

export const Route = createFileRoute("/_app/broadcasts/")({
  component: BroadcastsList,
});

function BroadcastsList() {
  const { activeIgAccount } = useAuth();
  const [rows, setRows] = useState<BroadcastRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeIgAccount) return;
    void load();
  }, [activeIgAccount?.id]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ ig_account_id: activeIgAccount!.id });
      const r = await api.get<{ broadcasts: BroadcastRow[] }>(`/broadcasts?${params}`);
      setRows(r.data.broadcasts);
    } catch {
      setError("Failed to load broadcasts");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section style={{ maxWidth: 1000, margin: "0 auto" }}>
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 28 }}>Broadcasts</h1>
        <Link to="/broadcasts/new" className="brut-button">
          + New broadcast
        </Link>
      </header>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: 24 }}>
        Send a one-shot message to every contact in a segment. Hard-capped
        per workspace until billing lands.
      </p>

      {error ? (
        <div className="brut-card" style={{ background: "#ffd7d7", padding: 10, marginBottom: 12 }}>
          {error}
        </div>
      ) : null}

      {loading ? (
        <p>Loading…</p>
      ) : rows.length === 0 ? (
        <div className="brut-card" style={{ textAlign: "center", padding: 24 }}>
          <p style={{ margin: "0 0 8px", fontWeight: 700 }}>No broadcasts yet</p>
          <p style={{ margin: 0, fontSize: 14, color: "var(--muted)" }}>
            Broadcasts send a single message to every contact in a segment.
            Build a segment first under <strong>Segments</strong>, then
            click "New broadcast".
          </p>
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
          {rows.map((b) => (
            <li
              key={b.id}
              className="brut-card"
              style={{
                padding: 14,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <Link
                  to="/broadcasts/$broadcastId"
                  params={{ broadcastId: b.id }}
                  style={{ fontWeight: 800, fontSize: 16, textDecoration: "none" }}
                >
                  {b.name}
                </Link>
                <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>
                  <StatusPill status={b.status} /> ·{" "}
                  {b.eligibleCount !== null
                    ? `${b.sentCount}/${b.eligibleCount} sent`
                    : "—"}
                  {b.skippedCount ? ` · ${b.skippedCount} skipped` : ""}
                  {b.failedCount ? ` · ${b.failedCount} failed` : ""}
                </div>
                {b.failureReason ? (
                  <div style={{ fontSize: 12, color: "#b40000", marginTop: 4 }}>
                    {b.failureReason}
                  </div>
                ) : null}
              </div>
              <Link
                to="/broadcasts/$broadcastId"
                params={{ broadcastId: b.id }}
                className="brut-button is-secondary"
                style={{ fontSize: 13, padding: "6px 10px" }}
              >
                Open
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function StatusPill({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: "#e5e5e5",
    scheduled: "#fff3a8",
    enumerating: "#cce5ff",
    sending: "#cce5ff",
    done: "#cffbcf",
    cancelled: "#e5e5e5",
    failed: "#ffd7d7",
  };
  const c = colors[status] ?? "#e5e5e5";
  return (
    <span
      style={{
        background: c,
        border: "2px solid var(--ink)",
        padding: "1px 8px",
        fontSize: 12,
        fontWeight: 800,
        textTransform: "uppercase",
      }}
    >
      {status}
    </span>
  );
}
